import { Eye, Undo2 } from "lucide-react";
import { useGetIdentity, useGetList, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { cn } from "@/lib/utils";

import { switchViewRole } from "../providers/supabase/authProvider";
import type { Role } from "../types";

type Identity = { realRoleKey?: string | null; previewRoleId?: number | null };

const useViewAs = () => {
  const { identity } = useGetIdentity();
  const id = identity as Identity | undefined;
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const isDeveloper = id?.realRoleKey === "developer";
  const switchTo = async (roleId: number | null) => {
    setBusy(true);
    try {
      await switchViewRole(roleId);
    } catch {
      notify("ra.notification.http_error", { type: "error" });
      setBusy(false);
    }
  };
  return {
    isDeveloper,
    previewRoleId: id?.previewRoleId ?? null,
    switchTo,
    busy,
  };
};

/** Developer's switch «view as role» (sidebar, profile) */
export const ViewAsSelect = ({ className }: { className?: string }) => {
  const translate = useTranslate();
  const { isDeveloper, previewRoleId, switchTo, busy } = useViewAs();
  const { data: roles = [] } = useGetList<Role>(
    "roles",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: isDeveloper },
  );
  if (!isDeveloper) return null;
  const own = roles.find((r) => r.key === "developer");
  return (
    <label
      className={cn(
        "flex flex-col gap-1 text-xs text-muted-foreground",
        className,
      )}
    >
      <span className="inline-flex items-center gap-1 font-semibold">
        <Eye className="size-3.5" />
        {translate("crm.view_as.label")}
      </span>
      <select
        disabled={busy}
        value={previewRoleId ?? own?.id ?? ""}
        onChange={(event) => {
          const value = Number(event.target.value);
          switchTo(value === own?.id ? null : value);
        }}
        className="h-8 rounded-lg border bg-card px-2 text-sm text-foreground"
      >
        {roles.map((role) => (
          <option key={role.id} value={role.id}>
            {role.name}
          </option>
        ))}
      </select>
    </label>
  );
};

/** Always visible while viewing as another role, with the way back */
export const ViewAsBanner = () => {
  const translate = useTranslate();
  const { isDeveloper, previewRoleId, switchTo, busy } = useViewAs();
  const { identity } = useGetIdentity();
  if (!isDeveloper || previewRoleId == null) return null;
  return (
    <div className="sticky top-0 z-40 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-[#e3ac45] px-4 py-2 text-sm font-semibold text-[#3d2a00]">
      <Eye className="size-4" />
      {translate("crm.view_as.banner", { role: identity?.roleName ?? "" })}
      <button
        type="button"
        disabled={busy}
        onClick={() => switchTo(null)}
        className="inline-flex items-center gap-1 rounded-full bg-[#3d2a00] px-3 py-1 text-xs font-bold text-[#f3d58f]"
      >
        <Undo2 className="size-3.5" />
        {translate("crm.view_as.back")}
      </button>
    </div>
  );
};
